
// ABOUTME: Main dashboard component displaying referral statistics and activity
// ABOUTME: Fully responsive design with mobile-first approach

import React from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useReferrals } from '@/hooks/useReferrals';
import { useJobRoles } from '@/hooks/useJobRoles';
import { useJobCount } from '@/hooks/useJobCount';
import { useIsMobile } from '@/hooks/use-mobile';
import DashboardHeader from './dashboard/DashboardHeader';
import StatsGrid from './dashboard/StatsGrid';
import QuickActions from './dashboard/QuickActions';
import RecentActivity from './dashboard/RecentActivity';
import FeaturedRoles from './dashboard/FeaturedRoles';
import ProfileManagement from './dashboard/ProfileManagement';

interface ReferrerDashboardProps {
  onViewChange?: (view: string) => void;
}

const ReferrerDashboard = ({
  onViewChange
}: ReferrerDashboardProps) => {
  const {
    user,
    profile,
    signOut
  } = useAuth();
  const {
    referrals,
    isLoading: referralsLoading
  } = useReferrals();
  const {
    jobRoles,
    isLoading: jobRolesLoading
  } = useJobRoles();
  const { data: jobCount, isLoading: jobCountLoading } = useJobCount();
  const isMobile = useIsMobile();

  // Get recent activity from referrals
  const recentActivity = referrals?.slice(0, 3).map((referral, index) => ({
    id: referral.id,
    message: `${referral.candidate_name} - ${referral.status.replace('_', ' ')}`,
    bonus: referral.bonus_amount || 0,
    time: new Date(referral.updated_at).toLocaleDateString(),
    type: referral.status === 'hired' ? 'success' : 'progress'
  })) || [];

  // Get top featured roles - fewer on mobile
  const maxRoles = isMobile ? 6 : 9;
  const featuredRoles = jobRoles
    ?.sort((a, b) => {
      const priorityOrder = { high: 3, medium: 2, low: 1 };
      const priorityDiff = (priorityOrder[b.priority as keyof typeof priorityOrder] || 0) - 
                          (priorityOrder[a.priority as keyof typeof priorityOrder] || 0);
      
      if (priorityDiff !== 0) return priorityDiff;
      return (parseFloat(b.bonus_amount) || 0) - (parseFloat(a.bonus_amount) || 0);
    })
    .slice(0, maxRoles) || [];

  const userInitials = profile ? `${profile.first_name?.[0] || ''}${profile.last_name?.[0] || ''}` : user?.email?.[0]?.toUpperCase() || 'U';
  const userName = profile?.first_name || 'User';

  if (referralsLoading || jobRolesLoading || jobCountLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 md:h-32 md:w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 text-sm md:text-base">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="w-full max-w-none">
        <div className="p-3 md:p-6 space-y-4 md:space-y-6">
          <div className="max-w-7xl mx-auto w-full">
            <DashboardHeader />

            {/* Main content area */}
            <div className="mt-8 space-y-4 md:space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-4">Hot Jobs</h2>
                <FeaturedRoles roles={featuredRoles} onViewChange={onViewChange} />
              </div>

              <ProfileManagement 
                user={user} 
                profile={profile} 
                userInitials={userInitials} 
                onSignOut={signOut} 
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferrerDashboard;
